import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { ChevronRight, ChevronLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const questions = [
  {
    id: "motivation_style",
    question: "What coaching style motivates you best?",
    options: [
      { value: "tough", label: "Tough Love", desc: "Direct, no-nonsense, drill sergeant energy" },
      { value: "calm", label: "Calm & Supportive", desc: "Encouraging, understanding, patient guidance" },
      { value: "competitive", label: "Competitive", desc: "Challenge me, push me to beat others" }
    ]
  },
  {
    id: "workout_vibe",
    question: "What's your ideal workout atmosphere?",
    options: [
      { value: "focus", label: "Focused & Intense", desc: "Minimal distractions, pure concentration" },
      { value: "hype", label: "High Energy & Hype", desc: "Music pumping, energy flowing" }
    ]
  },
  {
    id: "biggest_struggle",
    question: "What's your biggest fitness struggle?",
    options: [
      { value: "time", label: "Finding Time", desc: "Life is busy, hard to fit workouts in" },
      { value: "confidence", label: "Confidence", desc: "Not sure if I'm doing things right" },
      { value: "consistency", label: "Staying Consistent", desc: "I start strong but fade out" }
    ]
  },
  {
    id: "training_time",
    question: "When do you prefer to train?",
    options: [
      { value: "morning", label: "Morning", desc: "Rise and grind before the day starts" },
      { value: "afternoon", label: "Afternoon", desc: "Midday energy boost" },
      { value: "evening", label: "Evening", desc: "After work stress relief" }
    ]
  },
  {
    id: "music_preference",
    question: "What's your workout music vibe?",
    options: [
      { value: "instrumental", label: "Instrumental/Focus", desc: "Beats without lyrics" },
      { value: "hiphop", label: "Hip-Hop/Rap", desc: "Hard-hitting bars" },
      { value: "electronic", label: "Electronic/EDM", desc: "High BPM energy" },
      { value: "none", label: "No Music", desc: "I prefer silence or my own" }
    ]
  },
  {
    id: "core_reason",
    question: "Why are you really doing this?",
    options: [
      { value: "kids", label: "For My Family/Kids", desc: "I want to be an example" },
      { value: "health", label: "Health", desc: "I need to take care of myself" },
      { value: "discipline", label: "Building Discipline", desc: "Fitness is the foundation" },
      { value: "confidence", label: "Confidence", desc: "I want to feel good in my skin" }
    ]
  },
  {
    id: "quit_risk_score",
    question: "How likely are you to quit when it gets hard?",
    options: [
      { value: 1, label: "1 - Never", desc: "I've never quit anything" },
      { value: 2, label: "2 - Rarely", desc: "I push through most times" },
      { value: 3, label: "3 - Sometimes", desc: "Depends on the situation" },
      { value: 4, label: "4 - Often", desc: "I struggle when it gets tough" },
      { value: 5, label: "5 - Very Likely", desc: "I need all the help I can get" }
    ]
  },
  {
    id: "personal_why",
    question: "Write your personal 'WHY' - This will appear on your dashboard daily",
    type: "text"
  }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const current = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canProceed = answers[current.id] !== undefined && answers[current.id] !== "";

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [current.id]: value }));
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      await submitOnboarding();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const submitOnboarding = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding/complete`, answers);
      updateUser({ onboarding_completed: true, personal_why: answers.personal_why });
      toast.success("Profile set up! Let's start your transformation.");
      navigate("/pricing");
    } catch (error) {
      toast.error("Failed to save onboarding data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Coach Coop" className="h-10 w-10 rounded-sm" />
          <span className="font-black text-xl tracking-tighter">COACH COOP</span>
        </div>
        
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tighter mb-8 text-center">
              {current.question}
            </h2>

            {current.type === "text" ? (
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <Textarea
                    data-testid="onboarding-why-input"
                    placeholder="Example: I'm doing this for my kids. I want them to see their dad healthy and strong. I want to play with them without getting tired."
                    value={answers[current.id] || ""}
                    onChange={(e) => handleSelect(e.target.value)}
                    className="min-h-[150px] bg-input border-transparent focus:border-primary rounded-sm text-lg"
                  />
                  <p className="text-sm text-muted-foreground mt-4">
                    This will be shown to you every day as a reminder of why you started.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {current.options.map((option, i) => (
                  <motion.div
                    key={option.value}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card
                      data-testid={`onboarding-option-${option.value}`}
                      onClick={() => handleSelect(option.value)}
                      className={`cursor-pointer transition-all bg-card border-2 ${
                        answers[current.id] === option.value
                          ? "border-primary glow-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          answers[current.id] === option.value
                            ? "border-primary bg-primary"
                            : "border-muted-foreground"
                        }`}>
                          {answers[current.id] === option.value && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold uppercase tracking-tight">{option.label}</div>
                          <div className="text-sm text-muted-foreground">{option.desc}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className="uppercase font-bold tracking-widest rounded-sm"
          >
            <ChevronLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          
          <Button
            data-testid="onboarding-next-btn"
            onClick={handleNext}
            disabled={!canProceed || loading}
            className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isLastQuestion ? (
              "Complete Setup"
            ) : (
              <>Next <ChevronRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
